import { InputHTMLAttributes, forwardRef, Ref, useId } from 'react';
import clsx from 'clsx';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Required for accessibility */
  label: string;
  /** Option to visually show or hide the label */
  showLabel?: boolean;
  /** `text` is the default and recommended type */
  type?: 'text' | 'email' | 'password';
  placeholder?: string;
  /** Include for fields that require validation */
  error?: string;
}

function TextField(
  {
    label,
    showLabel = true,
    type = 'text',
    error,
    required,
    className,
    id,
    placeholder,
    ...props
  }: TextFieldProps,
  ref: Ref<HTMLInputElement>
) {
  const autoId = useId();
  const textFieldId = id ?? autoId;
  const textFieldClasses = clsx(
    'p-2 font-sans text-base text-text-dark placeholder:text-charcoal-500 border border-navy-300 rounded-lg',
    // focus state
    'focus:outline-none focus:border-focus focus:ring-1 focus:ring-focus focus:ring-offset-0',
    // placeholder
    'placeholder:text-charcoal-400',
    className
  );

  return (
    <div className="flex flex-col gap-1 font-sans">
      {showLabel && (
        <label
          htmlFor={textFieldId}
          className="font-semibold text-base text-black"
        >
          {label}
          {required && <span className="ml-1">*</span>}
        </label>
      )}
      <input
        id={textFieldId}
        ref={ref}
        required={required}
        className={textFieldClasses}
        type={type}
        placeholder={placeholder}
        {...props}
      />
      {error && (
        <p className="font-semibold text-sm text-error m-0 px-1">{error}</p>
      )}
    </div>
  );
}

// Explicitly setting `displayName` helps Storybook
// extract props from components that use forwardRef
const ForwardedTextField = forwardRef(TextField);
ForwardedTextField.displayName = 'TextField';

export { ForwardedTextField as TextField };
